import { fitLinkedText } from "./studioLinkedText";
import { alignHeadingWinglets, connectHeadingWinglets } from "./studioHeadings";
import { fitDescriptionFrames } from "./studioTextFit";
import { placeNewDescriptions, reconcileCmsItems } from "./studioContentLinks";
import { layoutDocument } from "./core/structure";
import { deepEqual, diffDocs } from "./core/docOps";

// What the canvas and every export show: the authored design plus layout
// derived from current CMS text and fonts (inline name/price rows, heading
// ornaments, flowing structure blocks). Derivation never changes authored
// data, never saves and never adds history (plan §2 "opening never authors").
const connected = (doc) => doc.pages.some((p) => p.elements.some((n) => n.inlineRow || n.nameTextHeight !== undefined));

export function derivePage(doc, page, content) {
  let single = { ...doc, pages: [page] };
  if (doc.menuKey === "takeout") single = fitDescriptionFrames(single, content);
  if (connected(single)) single = fitLinkedText(single, content);
  single = alignHeadingWinglets(single, content);
  single = layoutDocument(single);
  return keepIdentity(page, single.pages[0]);
}

// Reuse unchanged node objects so later diffs and memoized rendering skip them.
function keepIdentity(before, after) {
  if (before === after) return before;
  const byId = new Map(before.elements.map((n) => [n.id, n]));
  let changed = after.elements.length !== before.elements.length;
  const elements = after.elements.map((n, i) => {
    const prior = byId.get(n.id);
    if (prior && deepEqual(prior, n)) { if (before.elements[i] !== prior) changed = true; return prior; }
    changed = true;
    return n;
  });
  const { elements: a, ...restA } = before, { elements: b, ...restB } = after;
  if (!changed && deepEqual(restA, restB)) return before;
  return { ...after, elements };
}

// Per-page cache: a commit re-derives only the pages it touched. Anything that
// affects every page (styles, content, fonts, blocks) resets the cache.
export function createDisplayCache() {
  return { pages: new WeakMap(), key: null };
}
export function deriveDisplay(doc, content, cache, fontsToken = 0) {
  if (!doc) return doc;
  const key = [doc.styles, doc.textStyles, doc.blocks, doc.menuKey, content, fontsToken];
  if (!cache.key || cache.key.some((v, i) => v !== key[i])) { cache.pages = new WeakMap(); cache.key = key; }
  let changed = false;
  const pages = doc.pages.map((page) => {
    let derived = cache.pages.get(page);
    if (!derived) { derived = derivePage(doc, page, content); cache.pages.set(page, derived); }
    if (derived !== page) changed = true;
    return derived;
  });
  return changed ? { ...doc, pages } : doc;
}

// Explicit, previewable repairs that used to run silently on open. Each
// returns the repaired design or null when there is nothing to do.
export function proposedRepairs(doc, content, { display } = {}) {
  if (!doc) return [];
  const menusLoaded = Object.keys(content).some((k) => k.startsWith("menu:"));
  const repairs = [];
  const add = (id, label, detail, next) => {
    if (!next || next === doc) return;
    const ops = diffDocs(doc, next);
    if (ops.length) repairs.push({ id, label, detail, count: ops.length, pages: new Set(ops.map((o) => o.pageId || (o.path && o.path[0] === "page" && o.path[1]))).size, next });
  };
  add("connect-headings", "Connect heading ornaments", "Link recognizable flourish pairs to their section headings so they follow heading edits.", connectHeadingWinglets(doc));
  if (menusLoaded) {
    const reconciled = reconcileCmsItems(doc, content);
    const removed = doc.pages.reduce((sum, p, i) => sum + p.elements.length - reconciled.pages[i].elements.length, 0);
    add("remove-deleted", "Remove deleted CMS items", `${removed} element${removed === 1 ? "" : "s"} link to CMS items that no longer exist.`, removed ? reconciled : null);
    const placed = placeNewDescriptions(doc, content);
    const added = placed.pages.reduce((sum, p, i) => sum + p.elements.length - doc.pages[i].elements.length, 0);
    add("place-descriptions", "Place new CMS descriptions", `${added} description${added === 1 ? "" : "s"} were added in the CMS since this layout was made.`, added > 0 ? placed : null);
  }
  if (display && display !== doc) add("accept-layout", "Keep the current fitted layout", "Store the positions fitted to current CMS text and fonts, so older editors show the same layout.", display);
  if (doc.source === "original-pdf" && doc.paperImportVersion !== 2 && doc.pages.some((p) => p.width !== 612 || p.height !== 792))
    add("letter-pages", "Set pages to US Letter", "Early imports stored some pages at the wrong size.", { ...doc, paperImportVersion: 2, pages: doc.pages.map((p) => ({ ...p, width: 612, height: 792 })) });
  return repairs;
}

// Linked fields whose CMS record is missing from the loaded menus. They keep
// their last text and are listed, never removed silently.
export function unresolvedLinks(doc, content) {
  if (!doc || !Object.keys(content).some((k) => k.startsWith("menu:"))) return [];
  const out = [];
  doc.pages.forEach((page) => page.elements.forEach((n) => {
    if (!n.binding) return;
    const [type, id] = n.binding.split(":");
    const probe = type === "item" ? `item:${id}:name` : `section:${id}:header`;
    const menu = page.sourceMenu ? content[`menu:${page.sourceMenu}:loaded`] : true;
    if (menu && !Object.prototype.hasOwnProperty.call(content, probe)) out.push({ pageId: page.id, nodeId: n.id, binding: n.binding, text: n.text });
  }));
  return out;
}
